import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  minimizeCommentMutation,
  minimizeCommentMutation$rawResponse,
  ReportedContentClassifiers,
} from './__generated__/minimizeCommentMutation.graphql'

function toObjectPropertiesPatch(
  id: string,
  minimizedComment:
    | {
        isMinimized: boolean
        minimizedReason: string | null | undefined
      }
    | null
    | undefined,
): ObjectPropertiesPatch | undefined {
  if (!minimizedComment) return undefined
  return {
    id,
    values: {
      isMinimized: minimizedComment.isMinimized,
      minimizedReason: minimizedComment.minimizedReason,
    },
  }
}

export function minimizeCommentMutation({
  environment,
  input: {id, reason},
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {id: string; reason: ReportedContentClassifiers}
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  const optimisticResponse: minimizeCommentMutation$rawResponse = {
    minimizeComment: {
      clientMutationId: '',
      minimizedComment: {
        id,
        __typename: 'IssueComment',
        __isNode: 'IssueComment',
        isMinimized: true,
        minimizedReason: reason,
      },
    },
  }

  const mutationId = createMutationId()
  const optimisticPatch = toObjectPropertiesPatch(id, optimisticResponse.minimizeComment?.minimizedComment)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
      phase: 'optimistic',
      mutationId,
      data: optimisticPatch,
    })

  const disposable = commitMutation<minimizeCommentMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation minimizeCommentMutation($input: MinimizeCommentInput!) @raw_response_type {
        minimizeComment(input: $input) {
          clientMutationId
          minimizedComment {
            isMinimized
            minimizedReason
          }
        }
      }
    `,
    variables: {
      input: {subjectId: id, classifier: reason},
    },
    optimisticResponse,
    onCompleted: response => {
      if (response.minimizeComment === null || response.minimizeComment === undefined) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'error',
            mutationId,
            error: new Error('minimizeComment returned null or undefined'),
          }),
        )
        onError?.()
      } else {
        const patch = toObjectPropertiesPatch(id, response.minimizeComment.minimizedComment)
        if (patch) {
          dispatchMutationLifecycleEvent(
            new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
              phase: 'completed',
              mutationId,
              data: patch,
            }),
          )
        }
        onCompleted?.()
      }
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      onError?.(error)
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
